import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UptimeGeneralDialog,
  UptimeBox,
  UptimeTypography,
  HlxButton,
} from '@uptime/uptime-component';
import { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'utils/lumosTheme';
import { useDispatch, useSelector } from 'react-redux';
import { DelegationApprovalSetting, UserInfo } from 'models';
import { useOnBehalf, useUserData } from 'hooks';
import { RootState } from 'redux/store';
import { HomePath, LeaverPath, NewJoinerPath } from 'utils/routes';
import SearchUser from 'components/SearchUser';
import { getDelegation } from 'services/delegationApprovalService';
import { DelegationMessage, StyledCircularProgress } from './ActOnBehalfModal.style';
import UserService from 'services/userServices';
import { Box } from '@mui/material';
import { checkNewJoinerByEmail, getLeaverInfoByEmail, getNewJoinerInfoByEmail } from 'utils/function';
import onBehalfAction from 'redux/actions/onBehalfAction';

interface IActOnBehalfModal {
  isOpen: boolean;
  onClose: () => void;
}

const DELEGATE = 'delegate';

const ActOnBehalfModal: React.FC<IActOnBehalfModal> = ({
  isOpen,
  onClose,
}): JSX.Element => {
  const userData = useUserData();
  const dispatch = useDispatch();
  const { t } = useTranslation(['topNav', 'home']);
  const Peer = t('home:onBehalfOf.modal.searchUser.selectBox.peer');
  const DirectReport = t('home:onBehalfOf.modal.searchUser.selectBox.directReport');
  const Onboarding = t('home:onBehalfOf.modal.searchUser.selectBox.onboarding');
  const offBoarding = t('home:onBehalfOf.modal.searchUser.selectBox.offBoarding');

  const theme = useTheme() as typeof DefaultUptimeTheme;

  const { onDispatchOnBehalfUser } = useOnBehalf();

  const currentUser: UserInfo = useSelector((state: RootState) => state.baseReducer.currentUser);
  const { generalConfig } = useSelector((state: RootState) => state.configReducer);

  const [userOnBehalf, setUserOnBehalf] = useState<UserInfo | null>(null);
  const [delegationData, setDelegationData] = useState<DelegationApprovalSetting | null>(null);
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState<UserInfo[]>([]);


  const isUserDelegated = useMemo(() => {
    if (delegationData?.approvals) {
      return delegationData.user.uid === userOnBehalf?.uid;
    }
    return false;
  }, [delegationData, userOnBehalf]);

  const handleClose = () => {
    setUserOnBehalf(null);
    onClose();
  };

const handleSubmitActOnBehalf = async () => {
  setLoading(true);
  try {
    if (generalConfig?.newJoiner?.isNewJoinerAllowed) {
      const checkResult = await checkNewJoinerByEmail(userOnBehalf!.email);
      const newJoinerInfo = await getNewJoinerInfoByEmail(userOnBehalf!.email);
      if (!!checkResult && checkResult.result && !!newJoinerInfo) {
        onDispatchOnBehalfUser({ isOnBehalf: true, userOnBehalf: { ...newJoinerInfo, new_joiner: checkResult.result } });
        dispatch(onBehalfAction.setIsOnBehalfOfNewJoiner(true));
        handleClose();
        window.location.replace(NewJoinerPath);
        return;
      }
    }
    if (generalConfig?.leaver?.isLeaverEnabled && userOnBehalf?.offboarding) {
      const leaverInfo = await getLeaverInfoByEmail(userOnBehalf!.email);
      onDispatchOnBehalfUser({ isOnBehalf: true, userOnBehalf: { ...leaverInfo, leaver: !!leaverInfo } });
      dispatch(onBehalfAction.setIsOnBehalfOfLeaver(true));
      handleClose();
      window.location.replace(LeaverPath);
      return;
    }
    onDispatchOnBehalfUser({ isOnBehalf: true, userOnBehalf: userOnBehalf as UserInfo });
    handleClose();
    window.location.replace(HomePath);
  } catch (error) {
    console.error(error);
  } finally {
    setLoading(false);
  }
};

  const getUserOptions = async (manager_uid: string) => {
    try {
      const response = await UserService.getDelegationUser({ manager_uid });
      return response?.items || [];
    } catch (error) {
      return [];
    }
  };

  const getUserTag = async (activeUser: UserInfo) => {
    const isCurrentUserManager = currentUser.uid === activeUser.manager_uid;
    const isNewJoinerAllowed = generalConfig?.newJoiner?.isNewJoinerAllowed;
    const { result: isNewJoiner } = isCurrentUserManager && isNewJoinerAllowed
      ? await checkNewJoinerByEmail(activeUser.email)
      : { result: false };
    const isOffboardingUser = !!activeUser?.offboarding;

    if (!isCurrentUserManager) {
      return Peer;
    }

    if (isOffboardingUser) {
      return `${DirectReport} - ${offBoarding}`;
    }

    if (!!isNewJoiner) {
      return Onboarding;
    }

    return DirectReport;
  };

  const generateUserTags = async (users: UserInfo[]) => {
    let customNewOptions: UserInfo[] = [];
    const filteredUserOptions = users.filter(item => item.uid !== currentUser.uid);
  
    await Promise.all(filteredUserOptions.map(async (item) => {
      const userTag = await getUserTag(item);
      customNewOptions.push({
        ...item,
        user_tagAOB: userTag,
      });
    }));
  
    return customNewOptions;
  };

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoading(true);
        const { data }: { data: DelegationApprovalSetting[] } = await getDelegation({
          user_uid: currentUser?.uid,
          approvals: true,
          valid_time: true,
          by: DELEGATE
        });
        const hasUser = data?.length > 0;
        const user = hasUser ? data?.[0] : null;
        setDelegationData(user);

        const managerUID = userData?.manager_uid;
        const options = await getUserOptions(managerUID);
        const usersWithTag = await generateUserTags(options);
        setUserOptions(usersWithTag);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  const content = (
    <>
      {loading ? (
        <UptimeBox display='flex' justifyContent='center'>
          <StyledCircularProgress />
        </UptimeBox>
      ) : (
        <>
          <UptimeTypography id='txt-aobo-description'>
            {t('home:onBehalfOf.modal.open.description1')}
            <br />
            {t('home:onBehalfOf.modal.open.description2')}
          </UptimeTypography>
          <UptimeBox pt={4}>
            <SearchUser
              onSelect={setUserOnBehalf}
              userOptionList={userOptions}
            />
            {isUserDelegated && (
              <UptimeBox pt={1}>
                <DelegationMessage id='txt-aobo-delegation-message'>
                  {t('home:onBehalfOf.modal.open.hasDelegationMessage')}
                </DelegationMessage>
              </UptimeBox>
            )}
          </UptimeBox>
        </>
      )}
    </>
  );

  const buttonSubmit = (
    <HlxButton
      id='btn-aobo-submit'
      style={{ color: theme.uptimeColors.grayScales.white }}
      label={t('home:onBehalfOf.modal.open.submitButton')}
      onClick={handleSubmitActOnBehalf}
      disabled={loading || isUserDelegated || !userOnBehalf?.uid}
    />
  );

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <UptimeGeneralDialog
        id='dv-aobo-dialog'
        title={t('home:onBehalfOf.modal.open.title')}
        content={content}
        isOpen={isOpen}
        buttonSubmit={buttonSubmit}
        cancelLabel={t('home:onBehalfOf.modal.open.cancelButton')}
        commonStyles={{ width: '481px' }}
        justifyContentAction="center"
        headerBackgroundDefault={false}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default React.memo(ActOnBehalfModal);
